import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { toolCategories } from '../data/tools';
import { ToolCard } from '../components/Tools/ToolCard';
import { AdSense } from '../components/Layout/AdSense';
import { InFeedAd } from '../components/Layout/InFeedAd';
import { InArticleAd } from '../components/Layout/InArticleAd';
import { DisplayAd } from '../components/Layout/DisplayAd';

export const CategoryPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const category = toolCategories.find(cat => cat.id === categoryId);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Category not found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[category.icon];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">Home</Link>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">{category.name}</span>
        </div>

        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to all tools</span>
        </Link>

        {/* Category Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 mb-8 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <IconComponent className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {category.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {category.tools.length} tools available
              </p>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {category.description}
          </p>
          
          {/* Privacy Notice */}
          <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-green-800 dark:text-green-400 text-sm">
              🔒 All {category.name.toLowerCase()} are processed locally in your browser. 
              Your files never leave your device, ensuring complete privacy and security.
            </p>
          </div>
        </div>

        {/* Top Display Ad */}
        <DisplayAd className="mb-8" />

        {/* Tools Grid with In-feed Ads */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {category.tools.map((tool, index) => (
            <React.Fragment key={tool.id}>
              <ToolCard tool={tool} />
              {/* Insert In-feed ad after every 6 tools */}
              {(index + 1) % 6 === 0 && (
                <div className="md:col-span-2 lg:col-span-3">
                  <InFeedAd />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* In-article Ad Section */}
        <div className="my-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              About {category.name} Tools
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Our {category.name.toLowerCase()} tools are designed with privacy and security in mind. 
              Each tool processes your data locally, ensuring your files never leave your device.
            </p>
          </div>
          
          {/* In-article Ad */}
          <InArticleAd />
          
          <div className="text-center mt-8">
            <p className="text-gray-600 dark:text-gray-400">
              Whether you're working with images, documents, or text, our tools provide 
              professional-grade functionality while maintaining complete privacy.
            </p>
          </div>
        </div>

        {/* Bottom Display Ad */}
        <DisplayAd />

        {/* Related Categories */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Explore Other Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {toolCategories
              .filter(cat => cat.id !== categoryId)
              .slice(0, 3)
              .map((cat) => {
                const CatIcon = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[cat.icon];
                return (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="block p-6 bg-white dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all duration-200 group border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600"
                  >
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-800/40 transition-colors">
                        <CatIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {cat.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {cat.tools.length} tools
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {cat.description}
                    </p>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};